// @ts-strict-ignore
import React, { useContext } from 'react';
import { Controller } from 'react-hook-form';
import type { Option } from '@usealloy/component-library';
import {
  TextField,
  MultiSelect,
  useSnackBar,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import type { FormattedServiceConfigOption } from './types';
import {
  CredentialOwner,
} from './types';
import {
  StyledTextField,
} from './styles';


interface ServiceConfigOptionInputFieldProps {
  serviceConfigOptionMeta;
  formattedServiceConfigOption: FormattedServiceConfigOption;
  control;
  index: number;
  isUsingDefault: boolean;
  hasError: boolean;
  trigger;
  setValue;
  credentialOwner: CredentialOwner;
  isSecret: boolean;
  submitAutoSave?: () => {};
  onlySandboxEnabled?: boolean;
  isReadOnly?: boolean;
}

export const ServiceConfigOptionInputField = ({
  serviceConfigOptionMeta,
  formattedServiceConfigOption,
  control,
  index,
  isUsingDefault,
  hasError,
  trigger,
  setValue,
  credentialOwner,
  isSecret,
  submitAutoSave,
  onlySandboxEnabled,
  isReadOnly,
}: ServiceConfigOptionInputFieldProps) => {
  const addSnackBar = useSnackBar();
  const { agent: { isAgent } } = useContext(UserContext);

  const isAlloyCredential = credentialOwner === CredentialOwner.alloy;
  const isUnknownCredential = !credentialOwner || credentialOwner === CredentialOwner.unknown;

  // disabled for the customer agent editing the alloy or unknown owner field
  const disabled = isAgent && isSecret && (isAlloyCredential || isUnknownCredential);

  const allPossibleValue = serviceConfigOptionMeta?.possibleValues?.map(
    pv => ({
      label: pv.displayName,
      value: pv.serviceConfigPossibleValueId.toString(),
    }),
  );


  switch (serviceConfigOptionMeta.datatype) {
    case 'number':
    case 'decimal':
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            required: (!onlySandboxEnabled && serviceConfigOptionMeta.isRequired && !isUsingDefault) ? `${serviceConfigOptionMeta.displayName} cannot be empty.` as const : false,
          }}
          render={({ field: { onChange, value } }) => (
            <StyledTextField
              type="number"
              key={formattedServiceConfigOption.id}
              id={`${formattedServiceConfigOption.serviceConfigOptionId}`}
              step={serviceConfigOptionMeta.datatype === 'number' ? 1 : 0.1}
              label={serviceConfigOptionMeta.displayName}
              autoComplete="stopItChrome"
              required={!onlySandboxEnabled && serviceConfigOptionMeta.isRequired}
              error={hasError}
              value={value}
              disabled={isReadOnly}
              onChange={newValue => {
                onChange(Number(newValue.target.value));
                setValue(
                  `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                  false,
                );
                trigger(`formattedServiceConfigOptions.${index}.value` as const);
                if (submitAutoSave) {
                  submitAutoSave();
                }
              }}
            />
          )}
        />
      );
    case 'boolean':
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            required: (!onlySandboxEnabled && serviceConfigOptionMeta.isRequired && !isUsingDefault)
              ? `${serviceConfigOptionMeta.displayName} cannot be empty.` as const : false,
          }}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              label={serviceConfigOptionMeta.displayName}
              key={formattedServiceConfigOption.id}
              required={!onlySandboxEnabled && serviceConfigOptionMeta.isRequired}
              error={hasError}
              onChange={(newOption: Option) => {
                setValue(`formattedServiceConfigOptions.${index}.value` as const, newOption);
                setValue(
                  `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                  false,
                );
                onChange(newOption);
                if (submitAutoSave) {
                  submitAutoSave();
                }
              }}
              options={[
                { label: 'True', value: 'true' },
                { label: 'False', value: 'false' },
              ]}
              selectedOptions={value}
              isMulti={false}
              isDisabled={isReadOnly}
            />
          )}
        />
      );
    case 'singleselect':
    case 'multiselect':
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            validate: value => (onlySandboxEnabled || value?.length >= 1 || `${serviceConfigOptionMeta.displayName} cannot be empty.` as const),
          }}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              label={serviceConfigOptionMeta.displayName}
              key={formattedServiceConfigOption.id}
              required={!onlySandboxEnabled && serviceConfigOptionMeta.isRequired}
              error={hasError}
              onChange={newOptions => {
                if (serviceConfigOptionMeta.datatype === 'singleselect') {
                  onChange([newOptions]);
                  setValue(
                    `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                    false,
                  );
                  trigger(`formattedServiceConfigOptions.${index}.value` as const);
                  if (submitAutoSave) {
                    submitAutoSave();
                  }
                // @ts-ignore
                } else if (newOptions?.length >= 1) {
                  onChange(newOptions);
                  setValue(
                    `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                    false,
                  );
                  trigger(`formattedServiceConfigOptions.${index}.value` as const);
                  if (submitAutoSave) {
                    submitAutoSave();
                  }
                } else {
                  addSnackBar({
                    title: `${serviceConfigOptionMeta.displayName} cannot be empty.` as const,
                    type: 'danger',
                  });
                }
              }}
              options={allPossibleValue}
              isSearchable
              selectedOptions={value}
              visibleSelectedOptions={serviceConfigOptionMeta.datatype === 'multiselect'}
              isMulti={serviceConfigOptionMeta.datatype === 'multiselect'}
              isDisabled={isReadOnly}
            />
          )}
        />
      );
    default:
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            validate: value => (onlySandboxEnabled || value?.length >= 1 || `${serviceConfigOptionMeta.displayName} cannot be empty.` as const),
          }}
          render={({ field: { onChange, value } }) => {
            const finalValue = (isAgent && isSecret && isAlloyCredential) ? '•••••••••••' : value;
            return (
              <TextField
                type="text"
                key={formattedServiceConfigOption.id}
                id={`${serviceConfigOptionMeta.serviceConfigOptionId}`}
                label={serviceConfigOptionMeta.displayName}
                autoComplete="stopItChrome"
                isSecret={isSecret && !disabled}
                required={!onlySandboxEnabled && serviceConfigOptionMeta.isRequired}
                disabled={disabled || isReadOnly}
                error={hasError}
                value={finalValue}
                onBlur={() => {
                  trigger(`formattedServiceConfigOptions.${index}.value` as const);
                }}
                onChange={newValue => {
                  onChange(newValue);
                  setValue(
                    `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                    false,
                  );
                  trigger(`formattedServiceConfigOptions.${index}.value` as const);
                  if (submitAutoSave) {
                    submitAutoSave();
                  }
                }}
              />
            );
          }}
        />
      );
  }
};
